import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { createComponentLogger } from './logger';

const log = createComponentLogger('SupabaseAnonymous', 'public-access');

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'placeholder-key';

// Singleton instance - only created when first requested
let supabaseAnonInstance: SupabaseClient | null = null;

/**
 * Get the anonymous Supabase client for public data access
 *
 * This uses a lazy-loading singleton pattern to avoid creating the client
 * until it's actually needed, preventing "Multiple GoTrueClient instances" warnings.
 *
 * This client is specifically designed for public routes and pages that
 * should NOT require authentication. It:
 * - Does NOT persist sessions to localStorage
 * - Does NOT auto-refresh tokens
 * - Does NOT detect sessions in URL
 * - Never triggers authentication flows
 *
 * Use this client for:
 * - Public loan officer profiles (/p/{slug})
 * - Public pre-approval portals
 * - Any other public-facing data access
 *
 * DO NOT use this client for:
 * - Authenticated user operations
 * - Admin dashboard
 * - User management
 * - Any operations requiring RLS with auth.uid()
 */
export const getSupabaseAnon = (): SupabaseClient => {
  if (!supabaseAnonInstance) {
    // Only log in development
    if (import.meta.env.DEV) {
      log.debug('Creating anonymous Supabase client (lazy-loaded)', {
        urlConfigured: !!import.meta.env.VITE_SUPABASE_URL,
        keyConfigured: !!import.meta.env.VITE_SUPABASE_ANON_KEY,
        environment: import.meta.env.MODE
      });
    }

    // Create a no-op storage to completely isolate from authenticated client
    const noopStorage = {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {}
    };

    supabaseAnonInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        // Disable all authentication features for public access
        autoRefreshToken: false,   // Never try to refresh tokens
        persistSession: false,      // Never store session in localStorage
        detectSessionInUrl: false,  // Don't look for session in URL params
        storage: noopStorage,       // Use no-op storage to prevent any localStorage access
        storageKey: 'sb-anon-auth', // Use different key to avoid conflicts
        flowType: 'implicit'        // Simplest flow for anonymous access
      }
    });
  }

  return supabaseAnonInstance;
};

// Helper to check if Supabase is properly configured
export const isSupabaseConfigured = () => {
  return supabaseUrl !== 'https://placeholder.supabase.co' &&
         supabaseAnonKey !== 'placeholder-key' &&
         import.meta.env.VITE_SUPABASE_URL &&
         import.meta.env.VITE_SUPABASE_ANON_KEY;
};
